import type { RequiredFileModel, Stored, UUID } from "@overmap-ai/common"
import { BaseOfflineFileModelApiService } from "@overmap-ai/core"
import { cx } from "class-variance-authority"
import { useCallback } from "react"

import { interactiveCardCva } from "../../cva"
import { useFetchFileFromFileModel } from "../../hooks/utils"
import { AppSDK } from "../../sdk"
import type { RootState } from "../../store"
import { genericMemo } from "../../utils/optimization"
import { FileThumbnail } from "./FileThumbnail"

interface FileAttachmentThumbnailProps<TAttachment extends UUID<RequiredFileModel>> {
	attachment: Stored<TAttachment>
	service: BaseOfflineFileModelApiService<TAttachment["uuid"], TAttachment, RootState, AppSDK>
}

export const FileAttachmentThumbnail = genericMemo(function <TAttachment extends UUID<RequiredFileModel>>(
	props: FileAttachmentThumbnailProps<TAttachment>,
) {
	const { attachment, service } = props

	const [fetchedFile, failed] = useFetchFileFromFileModel(attachment, service)

	const handleView = useCallback(() => {
		if (!fetchedFile) return
		const objectUrl = URL.createObjectURL(fetchedFile)
		window.open(objectUrl, "_blank")
		URL.revokeObjectURL(objectUrl)
	}, [fetchedFile])

	return (
		<FileThumbnail
			className={cx({
				"cursor-pointer": !!fetchedFile,
				[interactiveCardCva()]: !!fetchedFile,
			})}
			onClick={handleView}
			file={fetchedFile}
			errored={failed}
		/>
	)
})
